// AI-GENERATED with alterations

import React, {useCallback, useEffect, useState, useMemo} from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    FormControl,
    Grid,
    IconButton,
    Input,
    InputAdornment,
    Typography,
    useToastProvider
} from '@deere/fuel-react';
import {DataGrid} from '@mui/x-data-grid';
import SearchIcon from "@material-ui/icons/Search";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import {Loading} from "../shared/Loading";
import {useAuthConfig} from "../shared/helper";

import {getSuggestedTeamMembers} from "../shared/services/teamService";
import {
    POTENTIAL_MEMBERS_TITLE,
    POTENTIAL_MEMBERS_SUBTITLE,
    SEARCH_PLACEHOLDER
} from "../shared/Constants";
import "../assets/PotentialMembers.scss";

/**
 * Component for displaying and managing potential team members
 * @param {Object} props - Component props
 * @param {string|null} props.supplierNumber - The supplier number to fetch potential members for
 * @param {Array} props.excludeRacfs - Array of RACF IDs to exclude from the list
 * @param {Function} props.onAddMember - Callback function when a member is added to the team
 */
export const PotentialTeamMembers = ({ supplierNumber = null, excludeRacfs = [], onAddMember }) => {
    const authAxios = useAuthConfig();
    const {enqueueToast} = useToastProvider();
    
    const [potentialMembers, setPotentialMembers] = useState([]);
    const [filteredMembers, setFilteredMembers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [pageSize, setPageSize] = useState(5);

    const handleAddMember = useCallback((member) => {
        if (onAddMember) {
            onAddMember(member);
            enqueueToast({
                variant: "success",
                title: "Member Added",
                message: `${member.firstName} ${member.lastName} added to team`,
                isCloseable: true
            });
        }
    }, [onAddMember, enqueueToast]);

    const columns = useMemo(() => [
        {
            field: 'fullName',
            headerName: 'Name',
            flex: 1,
            sortable: true,
            renderCell: (params) => {
                return (
                    <Typography variant="body2" fontWeight="medium">
                        {`${params.row.lastName}, ${params.row.firstName}`}
                    </Typography>
                );
            }
        },
        {
            field: 'racfId',
            headerName: 'User ID',
            flex: 0.6,
            sortable: true,
            renderCell(params) {
                return <Typography variant="body2">
                    {params.row.racfId}
                </Typography>;
            }
        },
        {
            field: 'note',
            headerName: 'Note',
            flex: 1.6,
            sortable: true,
            renderCell (params) {
                return <Typography variant="body2">
                    {params.row.note}
                </Typography>;
            }
        },
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 0.8,
            renderCell(params) {
                return <Button
                    variant="primary"
                    size="small"
                    startIcon={<PersonAddIcon />}
                    onClick={() => handleAddMember(params.row)}
                >
                    Add to Team
                </Button>;
            }
        }
    ], [handleAddMember]);

    /**
     * @param {string} supplierNumber 
     */
    const getPotentialTeamMembers = useCallback(async (supplierNumber) => {
        if (!supplierNumber)
            return;

        setIsLoading(true);

        try {
            let data = await getSuggestedTeamMembers(authAxios, supplierNumber);
            
            // Validate and clean the data
            const validatedData = Array.isArray(data) ? data.filter(member => 
                member && 
                typeof member === 'object' && 
                (member.racfId || member.racfID) &&
                member.firstName && 
                member.lastName
            ) : [];
            
            setPotentialMembers(validatedData);
            setFilteredMembers(validatedData);
            
        } catch (error) {
            console.error('Error fetching potential team members:', error);
            
            enqueueToast({
                variant: "error",
                title: "Error Loading Members",
                message: "Could not load potential team members. Please try again later.",
                isPersistent: false
            });
        } finally {
            setIsLoading(false);
        } // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSearch = useCallback((event) => {
        const query = event.target.value.toLowerCase();
        setSearchQuery(query);
        
        if (!query) {
            setFilteredMembers(potentialMembers);
        } else {
            const filtered = potentialMembers.filter(member =>
                [member.firstName, member.lastName, member.racfId, member.note].some(field =>
                    field && field.toLowerCase().includes(query)
                )
            );
            setFilteredMembers(filtered);
        }
    }, [potentialMembers]);

    useEffect(() => {
        getPotentialTeamMembers(supplierNumber);
    }, [supplierNumber, getPotentialTeamMembers]);

    const rowsData = useMemo(() => {
        return filteredMembers
            .map((member) => ({
                id: member.racfId || member.id || `member-${Math.random()}`,
                fullName: `${member.lastName}, ${member.firstName}`,
                ...member
            }))
            .filter(member => {
                // Ensure we have a valid racfId to check against excludeRacfs
                const racfId = member.racfId || member.racfID;
                return racfId && !excludeRacfs.includes(racfId);
            });
    }, [filteredMembers, excludeRacfs]);

    if (rowsData.length === 0 && !isLoading) {
        return null;
    }

    return <Card>
        <CardContent>
            <Box mb={3}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <Typography variant="h4" component="h4">
                            {POTENTIAL_MEMBERS_TITLE}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {POTENTIAL_MEMBERS_SUBTITLE}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                            <Input 
                                placeholder={SEARCH_PLACEHOLDER} 
                                value={searchQuery}
                                onChange={handleSearch}
                                endAdornment={
                                    <InputAdornment position="end">
                                        <IconButton aria-label="search">
                                            <SearchIcon style={{color: "#367c2b"}} />
                                        </IconButton>
                                    </InputAdornment>
                                }
                            />
                        </FormControl>
                    </Grid>
                </Grid>
            </Box>

            {isLoading
            ? <Loading />
            : <DataGrid
                rows={rowsData}
                columns={columns}
                pageSize={pageSize}
                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                rowsPerPageOptions={[5, 10, 20]}
                checkboxSelection={false}
                disableSelectionOnClick
                autoHeight
                pagination
            />}
        </CardContent>
    </Card>;
};
